import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquarePlus, Paperclip, Send, X } from 'lucide-react';
import { FILE_ACCEPT, MAX_FILES, formatFileSize } from '../constants';
import ConfirmModal from './ConfirmModal';

interface UpdateComposerProps {
  /** Testo della comunicazione (lo stato vive nella pagina, che poi lo invia). */
  body: string;
  onBodyChange: (value: string) => void;
  files: File[];
  onAddFiles: (picked: File[]) => void;
  onRemoveFile: (index: number) => void;
  /**
   * Invia. Restituisce `true` quando la comunicazione e partita davvero: solo
   * allora il riquadro si richiude, altrimenti resterebbe da riscrivere tutto
   * dopo un errore di rete.
   */
  onSend: () => boolean | void | Promise<boolean | void>;
  isSending?: boolean;
  /** Azzera testo e allegati alla chiusura del riquadro (stato della pagina). */
  onCancel: () => void;
  /**
   * Vero quando l'ultimo messaggio della cronologia e del richiedente: prima
   * di aprire il riquadro si chiede conferma, per non accumulare solleciti.
   */
  lastMessageIsMine?: boolean;
}

/**
 * "Aggiungi comunicazione": la richiesta e una pratica, non una chat, quindi
 * il campo di scrittura NON e aperto di default e non si invia con Invio.
 * Si apre da un bottone; se l'ultimo messaggio e gia del richiedente, prima
 * compare un avviso ("il canale ti rispondera") con "Aggiungi comunque".
 */
const UpdateComposer: React.FC<UpdateComposerProps> = ({
  body,
  onBodyChange,
  files,
  onAddFiles,
  onRemoveFile,
  onSend,
  onCancel,
  isSending = false,
  lastMessageIsMine = false,
}) => {
  const [open, setOpen] = useState(false);
  const [askConfirm, setAskConfirm] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  const openComposer = useCallback(() => {
    setAskConfirm(false);
    setOpen(true);
  }, []);

  // Il riquadro appena aperto prende il fuoco: chi ha premuto il bottone puo
  // scrivere subito, anche senza mouse.
  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  const handleOpenRequest = () => {
    if (lastMessageIsMine) {
      setAskConfirm(true);
      return;
    }
    openComposer();
  };

  const handleCancel = () => {
    setOpen(false);
    onCancel();
    openButtonRef.current?.focus();
  };

  // Invio riuscito: il riquadro si chiude e la comunicazione resta nella
  // cronologia. Invio fallito: resta aperto col testo, da ritentare.
  const handleSend = async () => {
    const sent = await onSend();
    if (sent) setOpen(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // consente di riselezionare lo stesso file
    if (picked.length > 0) onAddFiles(picked);
  };

  const canSend = !isSending && (body.trim().length > 0 || files.length > 0);

  if (!open) {
    return (
      <>
        <button
          ref={openButtonRef}
          type="button"
          onClick={handleOpenRequest}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 dark:border-indigo-700 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
        >
          <MessageSquarePlus className="w-4 h-4" />
          Aggiungi comunicazione
        </button>

        <ConfirmModal
          open={askConfirm}
          title="Hai già inviato una comunicazione"
          message="Il canale ti risponderà. Vuoi aggiungere altro?"
          confirmLabel="Aggiungi comunque"
          onConfirm={openComposer}
          onCancel={() => setAskConfirm(false)}
          returnRef={openButtonRef}
        />
      </>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
      <label
        htmlFor="nuova-comunicazione"
        className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
      >
        Usa questo spazio per informazioni utili a risolvere la richiesta
      </label>
      <textarea
        id="nuova-comunicazione"
        ref={textareaRef}
        value={body}
        onChange={(e) => onBodyChange(e.target.value)}
        rows={5}
        maxLength={5000}
        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors">
          <Paperclip className="w-3.5 h-3.5" />
          Allega file
          <input type="file" accept={FILE_ACCEPT} multiple onChange={handleFileInput} className="hidden" />
        </label>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Max {MAX_FILES} file, 10MB ciascuno
        </span>
      </div>

      {files.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="inline-flex items-center gap-1.5 max-w-full px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-200"
            >
              <Paperclip className="w-3.5 h-3.5 shrink-0 text-gray-400" />
              <span className="truncate">{f.name}</span>
              <span className="text-gray-400 dark:text-gray-500 shrink-0">{formatFileSize(f.size)}</span>
              <button
                type="button"
                onClick={() => onRemoveFile(i)}
                aria-label={`Rimuovi ${f.name}`}
                className="p-0.5 rounded text-gray-400 hover:text-red-500 transition-colors shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <button
          type="button"
          onClick={handleCancel}
          disabled={isSending}
          className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSending ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Invio…
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Invia comunicazione
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default UpdateComposer;
